// In-flight combat state machine. Engine-class + host-adapter pattern
// (cf. EffectLedger, PlayerResources). Owns phase transitions, sequences
// mutations to honor the codex "Fight" ordering (first strikes → sweep →
// normal strikes → sweep), drives the blocking prompts, and gates the
// defend/intercept network handshake. ALL board mutation goes through the
// adapter, which routes to existing GameBoard primitives
// (_applyDamageToTarget, _sweepDeadMinions, _applyTap, playerResources…).

import { PHASES, canTransition } from './combatPhases';
import { resolveFight, applyTankRedirect } from './resolveFight';

let counter = 0;
function newCombatId(seed) {
  counter += 1;
  return `cmb_${seed || 'x'}_${counter}`;
}

// Prompt kind for a restored phase: assignment asks the attacker to split
// power; otherwise the defender answers (intercept vs defend by path).
function promptKindForPhase(phase, isIntercept) {
  if (phase === PHASES.ASSIGNING) return 'assign';
  return isIntercept ? 'intercept' : 'defend';
}

// Which seat must answer a combat prompt. Pure precedence rules. The
// host adapter resolves the seats from live card instances and passes
// them in:
//   assign           → the attacker splits its power.
//   defend/intercept → the DEFENDING player. The seat derived from the
//     prompt's own subject (the attacked card's controller, or an
//     intercept candidate's controller) wins. It stays correct even
//     when the attacker's instance is unresolvable or its controller
//     flag desynced. Only when no subject seat is resolvable does the
//     seat opposite the attacker decide.
export function resolveActingSeat({ kind, attackerSeat, defenderSeat }) {
  if (kind === 'assign') return attackerSeat || 'p1';
  if (defenderSeat === 'p1' || defenderSeat === 'p2') return defenderSeat;
  return attackerSeat === 'p2' ? 'p1' : 'p2';
}

export class CombatEngine {
  constructor(adapter) {
    this.a = adapter;
    this.phase = PHASES.IDLE;
    this.fight = null; // { id, attacker, target, fromCell, defenders:[], isIntercept, projectile }
    this._prompt = null;
    // Combat ids that have already resolved. A resolved fight must never be
    // re-driven: an auto-declined fight resolves synchronously in the tick it
    // declared, so a stale pre-resolution record echoing back must not restore
    // and re-resolve it (that double-applied the hit). The orchestrator guards
    // too; this is the engine-level backstop that makes the double impossible.
    this._resolvedIds = new Set();
  }

  isBlocking() {
    return this.phase !== PHASES.IDLE && this.phase !== PHASES.DONE;
  }

  // Hard reset for an orphaned fight: the engine still blocks but the
  // durable combat record is gone (e.g. a teardown raced a reconnect), so
  // no decision can ever arrive to resolve it. Deliberately skips the
  // transition validator. This is the recovery hatch, not a phase.
  abort() {
    this.fight = null;
    this.phase = PHASES.IDLE;
    this._prompt = null;
  }

  // Restore an in-flight fight after a reconnect. Rebuilds the engine to the
  // given phase WITHOUT replaying declare side effects (the attacker was
  // already moved/tapped before the disconnect. That lives in the board
  // snapshot). `fight` mirrors the live shape:
  //   { id, attacker:{instanceId}, target, fromCell, defenders:[], isIntercept, projectile }
  restore(fight, phase) {
    if (!fight || !phase) return;
    // Already resolved. A stale echo of its pre-resolution record. Ignore it
    // so the engine can't be tricked into resolving the same fight twice.
    if (fight.id && this._resolvedIds.has(fight.id)) return;
    // Only the mid-fight, awaiting-input phases are restorable. A bad phase
    // would leave the engine wedged (isBlocking with no valid resolution).
    if (phase !== PHASES.DEFENDING && phase !== PHASES.INTERCEPTING && phase !== PHASES.ASSIGNING) {
      console.warn('[combat] restore ignored. Non-restorable phase', phase);
      return;
    }
    this.fight = {
      id: fight.id,
      attacker: fight.attacker,
      target: fight.target ?? null,
      fromCell: fight.fromCell ?? null,
      defenders: Array.isArray(fight.defenders) ? [...fight.defenders] : [],
      isIntercept: !!fight.isIntercept,
      projectile: !!fight.projectile,
    };
    this.phase = phase;
    this._prompt = { kind: promptKindForPhase(phase, fight.isIntercept), combatId: fight.id };
  }

  currentPrompt() {
    return this._prompt || null;
  }

  // Has this fight already resolved? The host reconcile loop asks this to tell
  // an orphaned durable record (engine resolved, terminal frame lost) apart
  // from a genuine mid-fight record awaiting restore after a reconnect.
  hasResolved(id) {
    return !!id && this._resolvedIds.has(id);
  }

  _to(next) {
    if (!canTransition(this.phase, next)) {
      throw new Error(`combat: illegal transition ${this.phase} -> ${next}`);
    }
    this.phase = next;
  }

  // ── Path A: declared attack ──
  declare(attacker, target, fromCell, opts = {}) {
    this._to(PHASES.DECLARED);
    this.fight = {
      id: (this.a.mintCombatId?.(attacker.instanceId) ?? newCombatId(attacker.instanceId)), attacker, target, fromCell,
      defenders: [], isIntercept: false, projectile: !!opts.projectile,
    };
    if (fromCell) this.a.moveAttackerTo(attacker.instanceId, fromCell);
    this.a.tapUnit(attacker.instanceId);
    this.a.fireTrigger('on_attack', attacker.instanceId, { target });

    this._to(PHASES.DEFENDING);
    // Codex Stealth: "their attacks cannot be defended" - a Stealth attacker's
    // declare opens NO defend window. Resolve in the same step (the Shoot
    // precedent), so the fight's liveness never hangs on the defending client
    // submitting a choiceless decline (qa#13: a stalled decline left the
    // persisted defend record wedged across reconnects). Adapters that can't
    // answer the query fall through to the prompt, where the client-side
    // auto-decline still covers the rule.
    if (this.a.hasActiveStealth?.(attacker.instanceId)) {
      this._assignOrResolve();
      return;
    }
    this._prompt = { kind: 'defend', combatId: this.fight.id, target };
    // openPrompt (host) records pendingCombat + drives the prompt to the
    // acting seat (combat:prompt). No separate combat:declare wire message.
    this.a.openPrompt(this._prompt);
  }

  // ── Path A2: Ranged shot. Codex Ranged: "Tap → Shoot a projectile ...
  // Strike the hit unit." A shot is an activated ability, NOT an attack.
  // no defend window, no intercept, no on_attack trigger, no strike-back
  // (projectile). Target is a unit or an avatar (projectiles never hit
  // sites). Lance still applies and breaks (codex Ranged: "a Lance will
  // inflict +1 damage and break").
  declareShot(attacker, target) {
    this._to(PHASES.DECLARED);
    this.fight = {
      id: (this.a.mintCombatId?.(attacker.instanceId) ?? newCombatId(attacker.instanceId)), attacker, target, fromCell: null,
      defenders: [], isIntercept: false, projectile: true, isShot: true,
    };
    this.a.tapUnit(attacker.instanceId);
    const power = this.a.getStatsFor(attacker.instanceId)?.atk || 0;
    const assignment = new Map();
    if (target?.kind === 'unit' && target.instanceId) assignment.set(target.instanceId, power);
    this._resolve(assignment);
  }

  addDefender(instanceId) {
    if (this.phase !== PHASES.DEFENDING) return;
    if (!this.fight.defenders.includes(instanceId)) this.fight.defenders.push(instanceId);
  }

  // Codex Attack 3.b: when a defender joins, "If the original target was a
  // unit, it may remain in the fight or not, as the defending player
  // chooses." `removeTarget` is that choice. Honored only when at least one
  // other defender joined (with no defenders the target IS the fight).
  commitDefenders(opts = {}) {
    if (this.phase !== PHASES.DEFENDING) return;
    if (opts.removeTarget && this.fight.defenders.length > 0) this.fight.targetRemoved = true;
    // Codex Defend: a defender TAPS to join the fight. The tap is a cost
    // paid HERE, server-side, on the validated survivor set. The client's
    // selection tap is a visual preview only (an optimistically broadcast
    // tap raced the defend intent and made the filter drop the defender).
    for (const id of this.fight.defenders) this.a.tapUnit(id);
    this._assignOrResolve();
  }

  // The full defender set for the fight: the originally-attacked unit is a
  // defender (codex Defender. "the originally attacked unit is also a
  // 'defender' if it stays in the fight") unless the defending player
  // withdrew it, plus any units the defender tapped in. Site/avatar targets
  // are not units, so they take life loss instead (handled in resolveFight).
  _effectiveDefenderIds() {
    const ids = [...this.fight.defenders];
    if (this.fight.targetRemoved && ids.length > 0) return ids;
    const t = this.fight.target;
    if (t && t.kind === 'unit' && t.instanceId && !ids.includes(t.instanceId)) ids.unshift(t.instanceId);
    return ids;
  }

  // Multiple defenders + nonzero power → prompt the attacker to split its
  // power. Otherwise resolve directly: one defender takes all the power,
  // zero defenders resolve with an empty assignment.
  _assignOrResolve() {
    const defenderIds = this._effectiveDefenderIds();
    const power = this.a.getStatsFor(this.fight.attacker.instanceId)?.atk || 0;
    if (defenderIds.length > 1 && power > 0) {
      this._to(PHASES.ASSIGNING);
      this._prompt = { kind: 'assign', combatId: this.fight.id, defenders: [...defenderIds] };
      this.a.openPrompt(this._prompt);
      return;
    }
    const assignment = new Map();
    if (defenderIds.length === 1) assignment.set(defenderIds[0], power);
    this._resolve(assignment);
  }

  assignDamage(assignment) {
    if (this.phase !== PHASES.ASSIGNING) return;
    this._resolve(assignment);
  }

  // ── Path B: move-without-attack → intercept ──
  offerIntercept(mover, cell, candidateIds) {
    if (!candidateIds || candidateIds.length === 0) return; // no combat
    this._to(PHASES.INTERCEPTING);
    this.fight = {
      id: (this.a.mintCombatId?.(mover.instanceId) ?? newCombatId(mover.instanceId)), attacker: mover, target: null, fromCell: cell,
      defenders: [], isIntercept: true, projectile: false,
    };
    this._prompt = { kind: 'intercept', combatId: this.fight.id, candidates: candidateIds };
    this.a.openPrompt(this._prompt);
  }

  pickInterceptors(instanceIds) {
    if (this.phase !== PHASES.INTERCEPTING) return;
    if (!instanceIds || instanceIds.length === 0) { this.declineIntercept(); return; }
    this.fight.defenders = [...instanceIds];
    // Codex Intercept: tapping is the cost of intercepting. Same server-side
    // payment as commitDefenders.
    for (const id of this.fight.defenders) this.a.tapUnit(id);
    this._assignOrResolve();
  }

  declineIntercept() {
    if (this.phase !== PHASES.INTERCEPTING) return;
    this.a.closePrompt();
    this._prompt = null;
    this._to(PHASES.IDLE);
    this.fight = null;
  }

  cancel() {
    // Pre-commitment only (DECLARED / DEFENDING / INTERCEPTING).
    if (this.phase === PHASES.DECLARED || this.phase === PHASES.DEFENDING || this.phase === PHASES.INTERCEPTING) {
      if (this.fight?.attacker) this.a.revertMove(this.fight.attacker.instanceId);
      this.a.closePrompt();
      this._prompt = null;
      this._to(PHASES.IDLE);
      this.fight = null;
    }
  }

  // ── Resolution ──
  _resolve(assignment) {
    this._to(PHASES.RESOLVING);
    const f = this.fight;
    // Damage events become first-class state: every applied damage /
    // life-loss is accumulated here and handed to the (optional)
    // publishEvents adapter hook, which writes them onto the durable
    // combat record. Both clients then render identical feedback from
    // one broadcast stream instead of inferring from stat mirrors.
    const events = [];
    // The mutation/trigger body calls into the full automation surface, which
    // can throw. The finally guarantees the fight ALWAYS terminates and the
    // prompt closes (→ host clears pendingCombat + emits combat:resolved), so
    // a bad trigger can never wedge the engine or strand the acting seat.
    let suspended = false;
    try {
      const aStats = this.a.getStatsFor(f.attacker.instanceId) || {};
      const defenderIds = this._effectiveDefenderIds();
      // An avatar DEFENDER can't die, but the damage assigned to it can be a
      // death blow. ResolveFight needs that to know a first-strike hit ends
      // the game before the normal window (so the avatar never strikes back).
      // The prediction must only fire when the hit would actually LAND: an
      // active Ward absorbs it at apply time (applyLifeLoss), and predicting
      // a death blow that the ward then swallows would wrongly cancel the
      // rest of the fight (qa#82 review finding; one rule for all three
      // avatar roles - defender, target, attacker).
      const landsDeathBlow = (instanceId, seat) => !!this.a.wouldDeathBlow?.(seat)
        && !(instanceId && this.a.isWarded?.(instanceId));
      const defenders = defenderIds.map((id) => {
        const d = { instanceId: id, ...(this.a.getStatsFor(id) || {}) };
        if (d.type === 'Avatar') d.deathBlow = landsDeathBlow(id, d.seat);
        return d;
      });

      // Snapshot enemy-minion effective powers BEFORE any damage lands, so a
      // kill trigger can tell whether the minion it killed was the strongest
      // enemy minion at fight start. The killed minion is off the board by the
      // time on_kill fires, so its power can't be re-read then. Sir Pellinore
      // "Whenever Sir Pellinore kills the strongest enemy minion, draw a card".
      const enemyPowers = this.a.enemyMinionPowers?.(f.attacker.instanceId) || {};

      // Same snapshot from the DEFENDING side (the attacker's own side of the
      // board) so a strike-BACK kill can credit the defender that swept the
      // attacker. Sir Pellinore defends, kills the strongest enemy minion. The
      // attacker itself. On strike-back and must draw. All defenders share a
      // side, so one lookup keyed off any defender resolves the attacker's side.
      const defenderSideEnemyPowers = defenderIds.length
        ? (this.a.enemyMinionPowers?.(defenderIds[0]) || {})
        : {};

      // Tank redirect (Sir Gawain): map each defended ally to the tank that
      // absorbs its incoming hit. Built BEFORE resolveFight so the fight math
      // knows a redirected ally takes no lethal combat damage (the tank
      // absorbs it) and therefore stays alive AND strikes back. The tank
      // never becomes a defender and never strikes back itself.
      const redirect = new Map();
      for (const d of defenders) {
        const tankId = this.a.tankRedirectFor?.(d.instanceId, f.attacker.instanceId);
        if (tankId && tankId !== d.instanceId) redirect.set(d.instanceId, tankId);
      }

      // An attacked avatar strikes back (codex: a fight is mutual and an
      // avatar is a unit), so resolveFight needs its power on the target
      // descriptor. The declare intent ships only kind/instanceId/seat.
      // `deathBlow` says a landed hit ENDS the game (at Death's Door, past the
      // grace turn): a first-striking attacker then kills before the avatar
      // strikes back, exactly as an FS kill on a minion defender does.
      const target = f.target || { kind: 'unit' };
      // `firstStrike` on an attacked avatar (an equipped Lance): its
      // strike-back then lands in the FS window, so a Death's Door attacker
      // dies to it before its own hit lands (codex FS ordering; the declare
      // intent ships only kind/instanceId/seat, so read it off the stats).
      const targetStats = target.instanceId ? (this.a.getStatsFor(target.instanceId) || {}) : {};
      const resolvedTarget = target.kind === 'avatar'
        ? {
          ...target,
          atk: targetStats.atk || 0,
          firstStrike: !!targetStats.firstStrike,
          deathBlow: landsDeathBlow(target.instanceId, target.seat),
        }
        : target;

      // An avatar ATTACKER mirrors the defender/target rule: its strike-back
      // is life loss on its controller, and a landed hit past Death's Door is
      // a death blow that ends the fight before any later window. resolveFight
      // consumes both `type` (lifeLoss routing) and `deathBlow` (qa#82).
      const attackerDesc = { instanceId: f.attacker.instanceId, ...aStats, projectile: f.projectile || aStats.projectile || false };
      if (attackerDesc.type === 'Avatar') attackerDesc.deathBlow = landsDeathBlow(f.attacker.instanceId, attackerDesc.seat);

      const outcome = resolveFight({
        attacker: attackerDesc,
        target: resolvedTarget,
        defenders,
        assignment,
        redirect,
      });

      // Sink each defended ally's assigned hit onto its tank and stamp the
      // redirected-damage flag so the tank's on_death draw fires when the
      // absorbed hit kills it. The ally's strike-back is already intact
      // (resolveFight kept it alive + striking via the `redirect` map).
      const absorbedBy = applyTankRedirect(outcome, (id) => redirect.get(id));
      for (const sinkId of absorbedBy) this.a.markRedirectAbsorb?.(sinkId);

      // Sequence mutations honoring codex Fight ordering. Deaths are realized
      // by the sweep (findDeadMinions) between sub-phases, so Ward / Lethal /
      // replacement effects in _applyDamageToTarget all apply.
      this._applyPhase(outcome.firstStrike, f, events);
      const swept1 = this.a.sweepDeadMinions() || [];
      this._applyPhase(outcome.normal, f, events);
      const swept2 = this.a.sweepDeadMinions() || [];

      const swept = [...swept1, ...swept2];
      // The strongest enemy power among the snapshot. A killed minion was
      // "the strongest" if its recorded power matches this maximum.
      const strongestEnemyPower = Object.values(enemyPowers).reduce(
        (m, p) => Math.max(m, p), -Infinity,
      );
      const strongestDefenderSidePower = Object.values(defenderSideEnemyPowers).reduce(
        (m, p) => Math.max(m, p), -Infinity,
      );
      for (const id of swept) {
        if (id === f.attacker.instanceId) {
          // The attacker was swept by strike-back. Credit each SURVIVING
          // defender (the strikers) with the kill. A defender that also died
          // isn't around to draw. killedWasStrongestEnemy is judged from the
          // defending side (the attacker was that side's enemy minion).
          const killedPower = defenderSideEnemyPowers[id];
          const killedWasStrongestEnemy = killedPower != null && killedPower === strongestDefenderSidePower;
          for (const d of defenderIds) {
            if (swept.includes(d)) continue;
            this.a.fireTrigger('on_kill', d, { killed: id, killedWasStrongestEnemy });
          }
          continue;
        }
        const killedPower = enemyPowers[id];
        const killedWasStrongestEnemy = killedPower != null && killedPower === strongestEnemyPower;
        this.a.fireTrigger('on_kill', f.attacker.instanceId, { killed: id, killedWasStrongestEnemy });
      }

      // Lance breaks on survivors whose OWN strike hit a unit (codex Lance:
      // "Bearer's next strike against units … then Lance breaks"). A surviving
      // attacker in a unit fight struck; a surviving defender only struck when
      // resolveFight recorded it as a striker (a shot unit or a no_strike_back
      // defender never strikes, so its Lance survives).
      const fought = defenders.length > 0 || f.target?.kind === 'avatar' || f.target?.kind === 'unit';
      if (fought) {
        if (!swept.includes(f.attacker.instanceId) && aStats._hasLance) this.a.breakLance(f.attacker.instanceId);
        const strikers = new Set(outcome.defenderStrikers || []);
        for (const d of defenders) {
          if (!swept.includes(d.instanceId) && d._hasLance && strikers.has(d.instanceId)) this.a.breakLance(d.instanceId);
        }
      }

      // Stealth is consumed on interaction (codex Stealth).
      this.a.consumeStealth(f.attacker.instanceId);
      for (const id of defenderIds) this.a.consumeStealth(id);

      // on_strike carries the declared target ONLY when that target was
      // actually struck. I.e. a damage / life-loss event landed on it. A
      // defended or intercepted fight routes the attacker's power onto the
      // defenders (dmgToDefenders), so the original target (an enemy Avatar
      // for Interrogator) took zero damage and must NOT be passed: a watcher
      // like Interrogator keys off the Avatar being hit, not merely targeted.
      const struckTarget = this._targetWasStruck(f, events) ? f.target : null;
      this.a.fireTrigger('on_strike', f.attacker.instanceId, { target: struckTarget });

      // Codex Fight: attacker and defenders "strike each other simultaneously".
      // A defender that struck the attacker also STRIKES it. When the attacker
      // is an enemy Avatar (avatars attack too), an ally-strikes-enemy-Avatar
      // watcher (Interrogator) must see the strike-back, not just the forward
      // strike. Fire on_strike from each defender against the attacker whenever
      // the attacker actually took strike-back damage (a warded/0-damage
      // strike-back landed nothing, so no strike is recorded).
      // An avatar attacker's strike-back is a lifeLoss beat carrying the
      // attacker's instanceId (a death blow lands with amount 0), so the
      // predicate must match both event shapes (qa#82).
      const attackerStruckBack = events.some(
        (e) => (e.kind === 'damage' && e.targetId === f.attacker.instanceId && e.amount > 0)
          || (e.kind === 'lifeLoss' && e.instanceId === f.attacker.instanceId && (e.amount > 0 || e.deathBlow)),
      );
      if (attackerStruckBack) {
        for (const id of defenderIds) {
          this.a.fireTrigger('on_strike', id, { target: { instanceId: f.attacker.instanceId } });
        }
      }
      this.a.gameOverCheck();
    } catch (e) {
      if (e?.ruleChoiceRequired) { suspended = true; throw e; }
      try { console.error('[combat] resolution failed; terminating fight', e); } catch { /* ignore */ }
    } finally {
      if (!suspended) {
        // Publish whatever ACTUALLY applied. Even a resolution that threw
        // mid-way already dealt its earlier damage, and both clients must
        // see it. Publish precedes closePrompt so the events land on the
        // record before the host clears it.
        if (events.length) {
          try { this.a.publishEvents?.(events); } catch (e3) { try { console.error('[combat] publishEvents failed', e3); } catch { /* ignore */ } }
        }
        try { this.a.closePrompt(); } catch (e2) { try { console.error('[combat] closePrompt failed', e2); } catch { /* ignore */ } }
        if (f?.id) this._resolvedIds.add(f.id);
        this._prompt = null;
        this.phase = PHASES.IDLE;
        this.fight = null;
      }
    }
  }

  // Did the declared target actually take a hit this resolution? The events
  // stream is the truth: a unit target shows up as a `damage` event on its
  // instanceId; an avatar / site target as a `lifeLoss` event (avatars carry
  // isAvatar, sites match on seat). A defended / intercepted / fully-warded
  // fight produces no such event, so the target was NOT struck. Warded /
  // replacement-absorbed beats publish amount 0 and don't count as a strike.
  _targetWasStruck(f, events) {
    const t = f?.target;
    if (!t || !events?.length) return false;
    if (t.kind === 'avatar') {
      // A death blow publishes amount 0 (no life left to lose) but IS a
      // landed strike on the avatar.
      // The attacker's own strike-back beat also carries isAvatar (qa#82)
      // and can share the seat shape (undefined === undefined on partial
      // descriptors), so it must never count as the TARGET being struck.
      return events.some((e) => e.kind === 'lifeLoss' && e.isAvatar && e.seat === t.seat
        && e.instanceId !== f?.attacker?.instanceId && (e.amount > 0 || e.deathBlow));
    }
    if (t.kind === 'site') {
      return events.some((e) => e.kind === 'lifeLoss' && !e.isAvatar && e.seat === t.seat && e.amount > 0);
    }
    if (t.instanceId) {
      return events.some((e) => e.kind === 'damage' && e.targetId === t.instanceId && e.amount > 0);
    }
    return false;
  }

  // applyDamage / applyLifeLoss may return the amount that ACTUALLY landed
  // after Ward / WOULD_TAKE_DAMAGE replacements (undefined → assume the full
  // amount). Events and on_damage triggers use the applied amount so the
  // staged playback and trigger conditions reflect what really happened.
  // a fully-absorbed hit publishes no damage event and fires no trigger.
  _applyPhase(phase, f, events) {
    const triggers = [];
    const apply = () => this._applyPhaseDamage(phase, f, events, (...args) => triggers.push(args));
    if (this.a.withDamageBatch) this.a.withDamageBatch(apply); else apply();
    for (const args of triggers) this.a.fireTrigger(...args);
  }

  _applyPhaseDamage(phase, f, events, fireTrigger) {
    const appliedOr = (ret, fallback) => (typeof ret === 'number' ? ret : fallback);
    for (const d of phase.dmgToDefenders) {
      // Ward state is read BEFORE applyDamage. The funnel consumes the ward.
      const warded = d.amount > 0 && !!this.a.isWarded?.(d.instanceId);
      const applied = appliedOr(this.a.applyDamage(d.instanceId, d.amount, f.attacker.instanceId), d.amount);
      if (applied <= 0) {
        // Ward absorbed the whole hit. The strike still HAPPENED, so publish a
        // zero-damage 'warded' beat. The overlay plays the attack animation
        // and shows it was warded. `power` carries the would-be damage so the
        // impact SOUND stays full-strength (ward changes the result, not the
        // swing). No on_damage triggers fire (no damage dealt).
        if (warded && !this.a.isWarded?.(d.instanceId)) events?.push({ kind: 'damage', targetId: d.instanceId, amount: 0, warded: true, power: d.amount, sourceId: f.attacker.instanceId });
        continue;
      }
      events?.push({ kind: 'damage', targetId: d.instanceId, amount: applied, sourceId: f.attacker.instanceId });
      fireTrigger('on_damage_dealt', f.attacker.instanceId, { target: d.instanceId, amount: applied });
      fireTrigger('on_damage_taken', d.instanceId, { source: f.attacker.instanceId, amount: applied });
    }
    if (phase.dmgToAttacker > 0) {
      const warded = !!this.a.isWarded?.(f.attacker.instanceId);
      // sourceId stays null. dmgToAttacker is the AGGREGATE of every striking
      // defender, so naming one of them would misattribute the damage-source
      // tags that gate WOULD_TAKE_DAMAGE replacements (a fire defender could
      // silently prevent the whole strike-back on a fire-immune attacker), and
      // an attacker's own Lethal still must not kill itself. `lethalSource` is
      // a separate channel carrying only "a Lethal defender is among these
      // strikers", which is all the codex Lethal force-kill needs. Same split
      // the client damage funnel makes between `source` and `lethalAttacker`.
      const applied = appliedOr(
        this.a.applyDamage(f.attacker.instanceId, phase.dmgToAttacker, null, { lethalSource: phase.lethalStrikerId || null }),
        phase.dmgToAttacker,
      );
      if (applied > 0) {
        events?.push({ kind: 'damage', targetId: f.attacker.instanceId, amount: applied, sourceId: null });
        fireTrigger('on_damage_taken', f.attacker.instanceId, { amount: applied });
      } else if (warded && !this.a.isWarded?.(f.attacker.instanceId)) {
        events?.push({ kind: 'damage', targetId: f.attacker.instanceId, amount: 0, warded: true, power: phase.dmgToAttacker, sourceId: null });
      }
    }
    for (const l of phase.lifeLoss) {
      // Ward on the struck site / avatar prevents the life loss but the strike
      // still animates. The struck card is the declared target, EXCEPT for a
      // defending avatar. It carries its own instanceId on the entry (the
      // declared target is then some other card in the same fight).
      // Every published lifeLoss beat carries `struckId` too: a DEFENDING
      // avatar is already a portrait on the combat scene (it's in the defender
      // list), so the overlay needs the id to land the beat on that portrait
      // instead of conjuring a second, duplicate "Life" tile for the avatar.
      const struckId = l.instanceId || f.target?.instanceId || (l.isAvatar ? this.a.avatarIdForSeat?.(l.seat) : null);
      const warded = !!(struckId && this.a.isWarded?.(struckId));
      // applyLifeLoss may return a number (applied) or { applied, deathBlow }
      // The avatar path reports a death blow, which applies 0 life (the
      // avatar is already at Death's Door) even though the strike landed and
      // ends the game.
      const ret = this.a.applyLifeLoss(l.seat, l.amount, l.isAvatar);
      const applied = appliedOr(ret, appliedOr(ret?.applied, l.amount));
      const deathBlow = typeof ret === 'object' && !!ret?.deathBlow;
      if (applied <= 0 && !deathBlow) {
        if (warded && !this.a.isWarded?.(struckId)) events?.push({ kind: 'lifeLoss', seat: l.seat, amount: 0, warded: true, power: l.amount, isAvatar: !!l.isAvatar, instanceId: struckId });
        continue;
      }
      if (deathBlow) {
        // A death blow applies no life (there is none left to lose) but the
        // strike absolutely happened. Publish a beat so the overlay stages
        // the hit and the fight doesn't broadcast an empty, unskippable
        // scene. `power` carries the swing for full-strength impact sound.
        events?.push({ kind: 'lifeLoss', seat: l.seat, amount: Math.max(0, applied), deathBlow: true, power: l.amount, isAvatar: true, instanceId: struckId });
        continue;
      }
      events?.push({ kind: 'lifeLoss', seat: l.seat, amount: applied, isAvatar: !!l.isAvatar, instanceId: struckId });
      // A site / avatar target taking life loss IS the attacker dealing
      // damage to it (codex Damage: an undefended site or avatar takes
      // life loss). Fire on_damage_dealt with the target's instanceId so
      // watch_target_filter automation sees the struck card. Realm-
      // Eater's "destroys sites it successfully attacks" keys off this.
      // Skipped when the struck card IS the attacker (an avatar attacker's
      // strike-back life loss): the dealer there is the defender, and the
      // attacker crediting itself would fire its own watchers (qa#82).
      if (struckId && struckId !== f.attacker.instanceId) {
        fireTrigger('on_damage_dealt', f.attacker.instanceId, { target: struckId, amount: applied });
        // A successfully-attacked SITE fires on_damage_taken on ITSELF so
        // its own "when attacked" automation (Wizard's Den discard) and
        // watchers (Sylvan Splendor dispel) react. Avatars are excluded.
        // player-life damage routes through the avatar-damage path, not
        // card automation. on_damage_dealt (above) keys off the attacker;
        // this keys off the struck site.
        if (f.target?.kind === 'site' && !l.isAvatar) {
          // `source` = the struck site (the watch target for both the self
          // path. Wizard's Den. And the watcher path. Sylvan Splendor);
          // the adapter resolves it to the card instance.
          fireTrigger('on_damage_taken', f.target.instanceId, { amount: applied, source: f.target.instanceId, attacker: f.attacker.instanceId });
        }
      } else if (struckId) {
        // The attacker's own strike-back (avatar attacker, qa#82): the old
        // card-damage path fired on_damage_taken on the attacker, so keep
        // that trigger surface intact - only the on_damage_dealt
        // self-attribution above is suppressed.
        fireTrigger('on_damage_taken', f.attacker.instanceId, { amount: applied });
      }
    }
  }
}
